import { useEffect, useMemo, useState } from "react";
import {
  parseLootBundle,
  type LootBundle,
  type LootRow,
} from "@guildmaster/reports/loot";
import { lootDisposition } from "../../../scripts/loot-assignment/loot-disposition.mjs";
import {
  formatLootOrder,
  formatLootGap,
} from "../../../scripts/loot-assignment/loot-presentation.mjs";
import type { Report } from "@guildmaster/reports";
import { api } from "./api";

export function LootExplorer({ report }: { report: Report }) {
  const [bundle, setBundle] = useState<LootBundle | null>(null),
    [error, setError] = useState("");
  const [lineupIndex, setLineup] = useState(0),
    [player, setPlayer] = useState("all"),
    [boss, setBoss] = useState("all"),
    [slot, setSlot] = useState("all"),
    [spec, setSpec] = useState("all"),
    [search, setSearch] = useState("");
  const [view, setView] = useState("main"),
    [page, setPage] = useState(0);
  const entry = report.datasets?.[0];
  useEffect(() => {
    let active = true;
    setBundle(null);
    setError("");
    if (entry)
      void api<unknown>(
        "/api/reports/" +
          report.id +
          "/" +
          report.revision +
          "/data/" +
          entry.name,
      )
        .then((value) => {
          if (active) setBundle(parseLootBundle(JSON.stringify(value)));
        })
        .catch((error) => {
          if (active)
            setError(
              error instanceof Error ? error.message : "Dataset unavailable",
            );
        });
    return () => {
      active = false;
    };
  }, [report.id, report.revision, entry?.name]);
  useEffect(
    () => setPage(0),
    [lineupIndex, player, boss, slot, spec, search, view],
  );
  const lineup = bundle?.lineups[lineupIndex];
  const rows = useMemo(
    () =>
      (view === "secondary"
        ? (lineup?.secondaryRows ?? [])
        : (lineup?.rows ?? [])
      ).filter(
        (row) =>
          (player === "all" || row.player === player) &&
          (boss === "all" || row.boss === boss) &&
          (slot === "all" || row.slot === slot) &&
          (spec === "all" ||
            lineup?.players.some(
              (p) =>
                p.name === row.player &&
                p.class +
                  " | " +
                  (view === "secondary" ? p.secondary?.spec : p.spec) ===
                  spec,
            )) &&
          [row.name, row.player, row.reason, row.slot]
            .join(" ")
            .toLowerCase()
            .includes(search.toLowerCase()),
      ),
    [lineup, view, player, boss, slot, spec, search],
  );
  if (error)
    return (
      <p role="alert" className="notice error">
        {error}
      </p>
    );
  if (!bundle || !lineup) return <p>Loading historical research…</p>;
  const bosses = [
      ...new Set(
        [...(lineup.coverage ?? []), ...lineup.rows].map((row) => row.boss),
      ),
    ],
    slots = [...new Set(lineup.rows.map((row) => row.slot))];
  const choose =
    (change: (value: string) => void) =>
    (event: React.ChangeEvent<HTMLSelectElement>) =>
      change(event.target.value);
  return (
    <section className="loot-explorer">
      <h2>Loot research</h2>
      <p className="notice">
        Historical research | {bundle.cutoff}. Provisional shortlists do not
        create claims, rolls or awards.
      </p>
      <div className="form-grid">
        <label>
          Observed lineup
          <select
            value={lineupIndex}
            onChange={(event) => {
              setLineup(Number(event.target.value));
              setPlayer("all");
              setSpec("all");
            }}
          >
            {bundle.lineups.map((lineup, index) => (
              <option key={lineup.fight} value={index}>
                {lineup.label} | Fight {lineup.fight}
              </option>
            ))}
          </select>
        </label>
        <label>
          Research view
          <select value={view} onChange={choose(setView)}>
            <option value="main">Main-spec shortlist and review</option>
            <option value="secondary">Secondary use after mains</option>
            <option value="coverage">All boss drops and coverage</option>
          </select>
        </label>
        <label>
          Boss
          <select value={boss} onChange={choose(setBoss)}>
            <option value="all">All bosses</option>
            {bosses.map((boss) => (
              <option key={boss}>{boss}</option>
            ))}
          </select>
        </label>
        {view !== "coverage" && (
          <>
            <label>
              Character
              <select value={player} onChange={choose(setPlayer)}>
                <option value="all">All recorded characters</option>
                {lineup.players.map((player) => (
                  <option key={player.name}>{player.name}</option>
                ))}
              </select>
            </label>
            <label>
              Class and spec
              <select value={spec} onChange={choose(setSpec)}>
                <option value="all">All recorded specs</option>
                {[
                  ...new Set(
                    lineup.players.map(
                      (p) =>
                        p.class +
                        " | " +
                        (view === "secondary"
                          ? (p.secondary?.spec ?? "Undeclared")
                          : p.spec),
                    ),
                  ),
                ].map((spec) => (
                  <option key={spec}>{spec}</option>
                ))}
              </select>
            </label>
            <label>
              Equipment slot
              <select value={slot} onChange={choose(setSlot)}>
                <option value="all">All slots</option>
                {slots.map((slot) => (
                  <option key={slot}>{slot}</option>
                ))}
              </select>
            </label>
          </>
        )}
        <label className="wide">
          Search item, character or evidence
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
      </div>
      <p className="fine">
        Filters retain the saved full-lineup positions and unknowns. Equipped
        gear is not a complete inventory.{" "}
        <a href={bundle.reportUrl} target="_blank" rel="noreferrer">
          Source report
        </a>
      </p>
      {view === "coverage" ? (
        <div className="evidence-grid">
          {(lineup.coverage ?? [])
            .filter(
              (row) =>
                (boss === "all" || row.boss === boss) &&
                [row.name, row.reason, row.boss]
                  .join(" ")
                  .toLowerCase()
                  .includes(search.toLowerCase()),
            )
            .map((row) => (
              <article className="evidence-card" key={row.itemId}>
                <h3>
                  <a href={row.url} target="_blank" rel="noreferrer">
                    {row.name}
                  </a>
                </h3>
                <p>
                  {row.boss} | {row.disposition?.label ?? row.state}
                </p>
                <p>{row.reason}</p>
                <details>
                  <summary>Roster coverage and unknowns</summary>
                  {row.evidence.map((player) => (
                    <p key={player.name}>
                      <strong>{player.name}</strong> | {player.status}
                      <br />
                      {player.reason}
                    </p>
                  ))}
                  {row.secondary?.map((player) => (
                    <p key={player.name}>
                      <strong>
                        {player.name} | {player.spec}
                      </strong>
                      <br />
                      {player.reason}
                    </p>
                  ))}
                </details>
              </article>
            ))}
        </div>
      ) : (
        <>
          <p>
            {rows.length} saved assessments | Page {page + 1} of{" "}
            {Math.max(1, Math.ceil(rows.length / 20))}
          </p>
          <div className="loot-rows">
            {rows.slice(page * 20, (page + 1) * 20).map((row) => (
              <LootItem
                key={row.player + ":" + row.itemId}
                row={row}
                source={
                  view === "secondary"
                    ? (lineup.players.find((p) => p.name === row.player)
                        ?.secondary?.source ?? undefined)
                    : lineup.players.find((p) => p.name === row.player)?.source
                }
              />
            ))}
          </div>
          {!rows.length && (
            <p className="notice">
              No matching assessments. Demand remains unknown.
            </p>
          )}
          <div className="form-actions">
            <button
              className="button"
              disabled={!page}
              onClick={() => setPage(page - 1)}
            >
              Previous
            </button>
            <button
              className="button"
              disabled={(page + 1) * 20 >= rows.length}
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}
      <details>
        <summary>History and model</summary>
        <p>
          Model revision: {bundle.planRevision ?? "Not supplied"}. Guide
          revision: {bundle.guideRevision ?? "Not supplied"}.
        </p>
        <p>
          {bundle.history
            ? bundle.history.reports +
              " source reports across " +
              bundle.history.nights +
              " observed nights. Duplicate uploads: " +
              bundle.history.duplicates +
              ". Phase-unknown pulls: " +
              bundle.history.unknownPhasePulls +
              "."
            : "Historical participation coverage was not supplied."}
        </p>
        <p>
          Observed nights do not establish expected attendance, tenure, complete
          loot ownership or allocation credit.
        </p>
      </details>
    </section>
  );
}
function LootItem({ row, source }: { row: LootRow; source?: string }) {
  const disposition = lootDisposition(row);
  return (
    <article className="panel loot-row">
      <div className="card-top">
        <h3>
          <a href={row.url} target="_blank" rel="noreferrer">
            {row.name}
          </a>
        </h3>
        <span className="badge">
          {row.secondary?.route ?? formatLootOrder(row.position, row.tied)}
        </span>
      </div>
      <p>
        {row.player} | {row.slot} | {row.boss}
      </p>
      <p>
        {disposition.label} | Item-level gap:{" "}
        {formatLootGap(row.delta, row.comparison)}
      </p>
      <p>{row.reason}</p>
      <details>
        <summary>Equipment and evidence</summary>
        <p>
          {row.current.length
            ? "Recorded equipment: " +
              row.current
                .map((item) => item.name + " (" + item.level + ")")
                .join(", ")
            : "No matching equipped snapshot was supplied."}
        </p>
        {row.comparison && <p>{row.comparison.basis}</p>}
        {row.plan && (
          <>
            <p>{row.plan.reason}</p>
            {row.plan.companions.map((item) => (
              <p key={item.id}>
                {item.slot}:{" "}
                <a href={item.url} target="_blank" rel="noreferrer">
                  {item.name}
                </a>
              </p>
            ))}
          </>
        )}
        {row.notes.map((note, index) => (
          <p key={index}>{note}</p>
        ))}
        {row.contenders.length > 0 && (
          <p>
            Saved contenders:{" "}
            {row.contenders
              .map(
                (player) => player.name + " (position " + player.position + ")",
              )
              .join(", ")}
            . Filtering never changes these positions.
          </p>
        )}
        <div className="sources">
          {source && (
            <a href={source} target="_blank" rel="noreferrer">
              Recorded character scope
            </a>
          )}
          {row.assessment?.sources.map((source) => (
            <a
              key={source.url}
              href={source.url}
              target="_blank"
              rel="noreferrer"
            >
              {source.author} | Retrieved {source.retrievedAt}
            </a>
          ))}
        </div>
      </details>
    </article>
  );
}
